import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, Home, CheckCircle2 } from 'lucide-react';
import api, { apiErrorText } from '../../utils/api';
import { formatPhoneDisplay } from '../../utils/phone';
import BillingTermSelector from '../../components/BillingTermSelector';
import {
  BillingTerm,
  DEFAULT_BILLING_TERM,
  formatUsd,
  isBillingTerm,
  isPricedPlan,
  termDiscountPercent,
  termListUsd,
  termPerMonthUsd,
  termTotalUsd,
} from '../../config/pricing';

interface OnboardingData {
  email: string;
  password: string;
  full_name: string;
  company_name: string;
  plan: string;
  description?: string;
  industry?: string;
  company_size?: string;
  work_model?: string;
}

interface EnterpriseContext {
  requestId: string;
  amount: string;
  currency: string;
  companyName: string;
}

interface FxBreakdown {
  baseAmount: number;
  baseCurrency: string;
  sourceAmount: number;
  sourceCurrency: string;
  amounts: Record<string, number>;
}

interface CheckoutResponse {
  payment_id: number;
  token: string;
  checkoutFormContent: string;
  chargedAmount?: number;
  chargedCurrency?: string;
  displayAmount?: number;
  displayCurrency?: string;
  fxRate?: number;
  fxBreakdown?: FxBreakdown;
}

interface PaymentSummary {
  chargedAmount?: number;
  chargedCurrency?: string;
  displayAmount?: number;
  displayCurrency?: string;
  fxRate?: number;
  fxBreakdown?: FxBreakdown;
}

const OnboardingCheckout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const checkoutContainerRef = useRef<HTMLDivElement>(null);
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingTerm>(() => {
    const fromState = (location.state as { billingTerm?: unknown } | null)?.billingTerm;
    if (isBillingTerm(fromState)) return fromState;
    const fromQuery = new URLSearchParams(location.search).get('billing_cycle');
    return isBillingTerm(fromQuery) ? fromQuery : DEFAULT_BILLING_TERM;
  });
  const [billing, setBilling] = useState({
    company_title: '',
    tax_number: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: 'Istanbul',
    country: 'Turkey',
  });
  
  const [checkoutHtml, setCheckoutHtml] = useState('');
  const [checkoutToken, setCheckoutToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [enterpriseContext, setEnterpriseContext] = useState<EnterpriseContext | null>(null);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const activated = searchParams.get('activated') === '1';
    const token = searchParams.get('token');

    if (!activated || !token) return;

    let isMounted = true;

    const runAutoLogin = async () => {
      try {
        setLoading(true);
        setError('');
        setSuccess(t('auth.checkout.autoLogin'));

        const tenantSlug = searchParams.get('tenant');
        localStorage.setItem('token', token);
        if (tenantSlug) {
          localStorage.setItem('tenantSlug', tenantSlug);
        }

        const meResponse = await api.get('/api/users/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const userData = meResponse.data;
        localStorage.setItem('user', JSON.stringify(userData));

        if (userData?.tenant_id && !tenantSlug) {
          const tenantResponse = await api.get('/api/tenants/me', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (tenantResponse?.data?.slug) {
            localStorage.setItem('tenantSlug', tenantResponse.data.slug);
          }
        }

        searchParams.delete('activated');
        searchParams.delete('token');
        const cleanedSearch = searchParams.toString();
        const cleanedUrl = `${location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ''}`;
        window.history.replaceState({}, '', cleanedUrl);

        const target = userData?.role === 'admin' ? '/app/admin' : '/app/dashboard';
        window.location.href = target;
      } catch (err) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenantSlug');
        if (isMounted) {
          setError(t('auth.checkout.autoLoginFailed'));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    runAutoLogin();

    return () => {
      isMounted = false;
    };
  }, [location.pathname, location.search, t]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isEnterprise = searchParams.get('enterprise') === 'true';
    const requestId = searchParams.get('request_id');
    const amount = searchParams.get('amount') || '1000';
    const currency = (searchParams.get('currency') || 'USD').toUpperCase();
    const companyName = decodeURIComponent(searchParams.get('company_name') || 'Enterprise Plan');
    const contactName = decodeURIComponent(searchParams.get('contact_name') || '');
    const contactEmail = decodeURIComponent(searchParams.get('contact_email') || '');
    const contactPhone = decodeURIComponent(searchParams.get('contact_phone') || '');

    if (isEnterprise && requestId) {
      setEnterpriseContext({
        requestId,
        amount,
        currency,
        companyName,
      });
      setOnboardingData({
        email: '',
        password: '',
        full_name: '',
        company_name: companyName,
        plan: 'enterprise',
        description: '',
      });
      setBilling((prev) => ({
        ...prev,
        email: contactEmail,
        contact_name: contactName,
        company_title: companyName,
        phone: contactPhone || prev.phone,
      }));
      setCheckoutHtml('');
      setCheckoutToken('');
      setPaymentSummary(null);
      setError('');
      setSuccess('');
      return;
    }

    setEnterpriseContext(null);
    setPaymentSummary(null);

    const data = location.state?.onboardingData as OnboardingData;
    if (!data || !data.plan || data.plan === 'free') {
      navigate('/onboarding');
      return;
    }
    setOnboardingData(data);
    setBilling((prev) => ({
      ...prev,
      email: data.email,
      contact_name: data.full_name,
      company_title: data.company_name,
    }));
  }, [location.search, location.state, navigate]);

  useEffect(() => {
    const container = checkoutContainerRef.current;
    if (!container) return;

    if (typeof window !== 'undefined') {
      try {
        const globalWindow = window as any;
        if (globalWindow.iyziInit) {
          globalWindow.iyziInit = undefined;
        }
        if (globalWindow.iyziJSTag) {
          try {
            globalWindow.iyziJSTag.remove?.();
          } catch (err) {
            // ignore removal errors
          }
          globalWindow.iyziJSTag = undefined;
        }
      } catch (err) {
        console.warn('iyzico state reset failed', err);
      }
    }

    container.innerHTML = '';
    if (!checkoutHtml) return;

    const tempWrapper = document.createElement('div');
    tempWrapper.innerHTML = checkoutHtml;

    const nodes = Array.from(tempWrapper.childNodes);
    nodes.forEach((node) => {
      if (node.nodeName === 'SCRIPT') {
        const script = document.createElement('script');
        const original = node as HTMLScriptElement;
        Array.from(original.attributes).forEach((attr) => {
          script.setAttribute(attr.name, attr.value);
        });
        script.text = original.text || '';
        container.appendChild(script);
      } else {
        container.appendChild(node);
      }
    });
  }, [checkoutHtml]);

  const isEnterpriseMode = onboardingData?.plan === 'enterprise' && !!enterpriseContext;

  const handleBillingChange = (field: keyof typeof billing, value: string) => {
    setBilling((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onboardingData) return;

    if (isEnterpriseMode) {
      setLoading(true);
      setError('');
      setSuccess('');
      setCheckoutHtml('');
      setCheckoutToken('');
      try {
        const response = await api.post<CheckoutResponse>('/api/payments/enterprise/checkout', {
          request_id: Number(enterpriseContext?.requestId),
        });
        if (!response.data?.checkoutFormContent) {
          setError(t('auth.checkout.paymentError'));
        } else {
          setCheckoutHtml(response.data.checkoutFormContent);
          setCheckoutToken(response.data.token);
          setPaymentSummary({
            chargedAmount: response.data.chargedAmount,
            chargedCurrency: response.data.chargedCurrency,
            displayAmount: response.data.displayAmount,
            displayCurrency: response.data.displayCurrency,
            fxRate: response.data.fxRate,
            fxBreakdown: response.data.fxBreakdown,
          });
          setSuccess(t('auth.checkout.enterpriseReady'));
        }
      } catch (err: any) {
        setError(apiErrorText(err.response?.data?.detail) || t('auth.checkout.paymentError'));
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setCheckoutHtml('');
    setCheckoutToken('');

    try {
      const response = await api.post<CheckoutResponse>('/api/payments/onboarding/checkout', {
        email: onboardingData.email,
        password: onboardingData.password,
        full_name: onboardingData.full_name,
        company_name: onboardingData.company_name,
        plan_type: onboardingData.plan,
        billing_cycle: billingCycle,
        billing,
        description: onboardingData.description,
        industry: onboardingData.industry,
        company_size: onboardingData.company_size,
        work_model: onboardingData.work_model,
      });

      setCheckoutHtml(response.data.checkoutFormContent);
      setCheckoutToken(response.data.token);
      setPaymentSummary({
        chargedAmount: response.data.chargedAmount,
        chargedCurrency: response.data.chargedCurrency,
        displayAmount: response.data.displayAmount,
        displayCurrency: response.data.displayCurrency,
        fxRate: response.data.fxRate,
        fxBreakdown: response.data.fxBreakdown,
      });
      setSuccess(t('auth.checkout.paymentSuccess'));
    } catch (err: any) {
      setError(apiErrorText(err.response?.data?.detail) || t('auth.checkout.paymentError'));
    } finally {
      setLoading(false);
    }
  };

  if (!onboardingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lotus-gold mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const planLabel = isEnterpriseMode
    ? t('auth.checkout.enterprisePlan')
    : t(`auth.checkout.planNames.${onboardingData.plan}`, onboardingData.plan);
  const formatAmount = (value?: number, currency?: string) => {
    if (value == null || !currency) return null;
    return `${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  };

  const planPrice = isEnterpriseMode
    ? formatAmount(paymentSummary?.displayAmount, paymentSummary?.displayCurrency) || `${enterpriseContext?.amount || ''} ${enterpriseContext?.currency || ''}`
    : isPricedPlan(onboardingData.plan)
      ? formatUsd(termTotalUsd(onboardingData.plan, billingCycle))
      : '';

  const chargedText = isEnterpriseMode
    ? formatAmount(paymentSummary?.chargedAmount, paymentSummary?.chargedCurrency || 'TRY')
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-lotus-cream via-white to-lotus-gold/10 p-4">
      <div className="absolute top-6 left-6">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-lotus-gold transition-colors"
        >
          <Home size={18} />
          <span>{t('auth.backToHome')}</span>
        </Link>
      </div>

      <div className="max-w-5xl mx-auto pt-20">
        <div className="text-center mb-8 fade-in">
          <Link to="/" className="inline-block mb-4 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.png" 
              alt="LotusRecruiter" 
              className="w-32 h-32 mx-auto object-contain"
            />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.checkout.title')}</h1>
          <p className="text-gray-600">
            {t('auth.checkout.planSummary', {
              company: onboardingData.company_name,
              plan: planLabel,
              price: planPrice,
              period: t(`pricingTerms.${billingCycle}`),
            })}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <form className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4 lg:col-span-3 fade-in" onSubmit={handleSubmit}>
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="text-lotus-gold" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{t('auth.checkout.billingInfo')}</h2>
                <p className="text-sm text-gray-500">{t('auth.checkout.secureNote')}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">{t('auth.checkout.companyTitle')}</label>
                <input className="input" value={billing.company_title} onChange={(e) => handleBillingChange('company_title', e.target.value)} required />
              </div>
              <div>
                <label className="label">{t('auth.checkout.taxNumber')}</label>
                <input className="input" value={billing.tax_number} onChange={(e) => handleBillingChange('tax_number', e.target.value)} />
              </div>
              <div>
                <label className="label">{t('auth.checkout.contactName')}</label>
                <input className="input" value={billing.contact_name} onChange={(e) => handleBillingChange('contact_name', e.target.value)} required />
              </div>
              <div>
                <label className="label">{t('common.email')}</label>
                <input className="input" type="email" value={billing.email} onChange={(e) => handleBillingChange('email', e.target.value)} required />
              </div>
              <div>
                <label className="label">{t('common.phone')}</label>
                <input
                  className="input"
                  type="text"
                  value={billing.phone}
                  onChange={(e) => handleBillingChange('phone', e.target.value)}
                  placeholder={t('auth.checkout.phonePlaceholder')}
                  required
                />
              </div>
              <div>
                <label className="label">{t('auth.checkout.city')}</label>
                <input className="input" value={billing.city} onChange={(e) => handleBillingChange('city', e.target.value)} required />
              </div>
              <div className="md:col-span-2">
                <label className="label">{t('auth.checkout.address')}</label>
                <textarea className="input" value={billing.address} onChange={(e) => handleBillingChange('address', e.target.value)} rows={3} />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm flex items-center gap-2">
                <CheckCircle2 size={16} />
                {success}
              </div>
            )}

            <button type="submit" className="btn-primary w-full" disabled={loading}>
              {loading
                ? t('auth.checkout.preparingForm')
                : t('auth.checkout.payWithIyzico')}
            </button>
          </form>

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4 lg:col-span-2 fade-in">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{t('auth.checkout.paymentSummary')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('auth.checkout.plan')}:</span>
                  <span className="font-semibold text-gray-900">{planLabel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('auth.checkout.amount')}:</span>
                  <span className="font-semibold text-gray-900">{planPrice}</span>
                </div>
                {chargedText && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t('auth.checkout.localCharge')}:</span>
                    <span className="font-semibold text-lotus-gold">{chargedText}</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between">
                  <span className="text-gray-900 font-semibold">{t('auth.checkout.total')}:</span>
                  <span className="text-lotus-gold font-bold text-lg">{planPrice}</span>
                </div>
              </div>
            </div>

            {!isEnterpriseMode && (
              <div className="mb-6 space-y-2">
                <BillingTermSelector
                  value={billingCycle}
                  onChange={(term) => {
                    setBillingCycle(term);
                    setCheckoutHtml('');
                    setCheckoutToken('');
                    setPaymentSummary(null);
                    setSuccess('');
                    setError('');
                  }}
                  containerClassName="flex flex-wrap items-center bg-white p-1 rounded-xl w-max shadow-sm border border-gray-100"
                  activeClassName="bg-lotus-cream/50 text-gray-900 shadow-sm"
                />
                {isPricedPlan(onboardingData.plan) && (
                  <p className="text-xs text-gray-500">
                    {t('pricingTerms.perMonthEquivalent', {
                      amount: formatUsd(termPerMonthUsd(onboardingData.plan, billingCycle)),
                    })}
                    {termDiscountPercent(billingCycle) > 0 && (
                      <span className="text-green-700 font-medium">
                        {' · '}
                        {t('pricingTerms.save', { percent: termDiscountPercent(billingCycle) })}
                        {' · '}
                        {t('pricingTerms.insteadOf', { original: formatUsd(termListUsd(onboardingData.plan, billingCycle)) })}
                      </span>
                    )}
                  </p>
                )}
                <p className="text-xs text-gray-500">{t('pricingTerms.annualOnly')}</p>
              </div>
            )}

            {paymentSummary && paymentSummary.fxBreakdown && (
              <div className="border border-lotus-gold/30 bg-lotus-cream/40 rounded-xl p-4 space-y-2 text-sm text-gray-700">
                <p className="font-semibold text-gray-800">
                  {t('auth.checkout.fxNotice', {
                    local: paymentSummary.chargedCurrency || 'TRY',
                    display: planPrice,
                    charged: chargedText,
                  })}
                </p>
                {paymentSummary.fxRate && paymentSummary.displayCurrency && (
                  <p>
                    {t('auth.checkout.fxRate', {
                      displayCurrency: paymentSummary.displayCurrency,
                      fxRate: paymentSummary.fxRate.toFixed(4),
                      chargedCurrency: paymentSummary.chargedCurrency || 'TRY',
                    })}
                  </p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-gray-600">
                  {Object.entries(paymentSummary.fxBreakdown.amounts).map(([code, value]) => (
                    <div key={code} className="border border-gray-100 rounded-lg px-3 py-2 flex justify-between">
                      <span>{code}</span>
                      <span>{value.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500">
                  {t('auth.checkout.fxDisclaimer')}
                </p>
              </div>
            )}

            {checkoutToken && (
              <div className="border-t pt-4">
                <p className="text-xs text-gray-500 mb-2">{t('auth.checkout.tokenLabel')}: <span className="font-mono">{checkoutToken}</span></p>
                <div ref={checkoutContainerRef} className="checkout-form-container" />
                <p className="text-xs text-gray-400 mt-2">
                  {t('auth.checkout.tokenHelp')}
                </p>
              </div>
            )}

            <div className="border-t pt-4">
              <p className="text-xs text-gray-500">
                {t('auth.checkout.accountAfterPayment')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingCheckout;
